import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import Input from '../../components/common/Input';
import LanguageMultiSelect from '../../components/common/LanguageMultiSelect';
import { MEETING_LANGUAGE_OPTIONS, parseLanguageCsv } from '../../utils/languageOptions';
import { Country, State, City } from 'country-state-city';
import { 
  Plus, 
  UserPlus, 
  Edit3, 
  Power,
  Mail,
  Phone,
  Briefcase,
  User,
  Search,
} from 'lucide-react';

const AdminSpeakers = () => {
  const { theme } = useTheme();
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '',
    stateCode: '',
    city: '',
    state: '',
    country: '',
    designation: '',
    company: '',
    avatar: '',
    language: [],
    specialization: '',
    publicContactNumber: '',
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSpeakers();
  }, []);

  const countries = Country.getAllCountries();
  const states = formData.countryCode ? State.getStatesOfCountry(formData.countryCode) : [];
  const cities = formData.countryCode && formData.stateCode
    ? City.getCitiesOfState(formData.countryCode, formData.stateCode)
    : [];

  const resolveCountryCodeByName = (countryName) =>
    countries.find((country) => country.name === countryName)?.isoCode || '';

  const resolveStateCodeByName = (countryCode, stateName) => {
    if (!countryCode || !stateName) {
      return '';
    }
    return State.getStatesOfCountry(countryCode).find((state) => state.name === stateName)?.isoCode || '';
  };

  const fetchSpeakers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/speakers');
      setSpeakers(response.data);
    } catch (error) {
      console.error('Failed to fetch speakers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (speaker = null) => {
    setSuccessMessage('');
    if (speaker) {
      setEditingSpeaker(speaker);
      const countryCode = resolveCountryCodeByName(speaker.country || '');
      const stateCode = resolveStateCodeByName(countryCode, speaker.state || '');
      setFormData({
        name: speaker.name || '',
        email: speaker.email || '',
        phone: speaker.phone || '',
        countryCode,
        stateCode,
        city: speaker.city || '',
        state: speaker.state || '',
        country: speaker.country || '',
        designation: speaker.designation || '',
        company: speaker.company || '',
        avatar: speaker.avatar || '',
        language: parseLanguageCsv(speaker.language),
        specialization: speaker.specialization || '',
        publicContactNumber: speaker.publicContactNumber || '',
      });
    } else {
      setEditingSpeaker(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        countryCode: '',
        stateCode: '',
        city: '',
        state: '',
        country: '',
        designation: '',
        company: '',
        avatar: '',
        language: [],
        specialization: '',
        publicContactNumber: '',
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSpeaker(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      countryCode: '',
      stateCode: '',
      city: '',
      state: '',
      country: '',
      designation: '',
      company: '',
      avatar: '',
      language: [],
      specialization: '',
      publicContactNumber: '',
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        language: Array.isArray(formData.language) ? formData.language.join(', ') : '',
      };

      if (editingSpeaker) {
        const response = await api.patch(`/admin/speakers/${editingSpeaker.id}`, payload);
        setSuccessMessage(response.data?.message || 'Speaker updated successfully.');
      } else {
        const response = await api.post('/admin/speakers', payload);
        setSuccessMessage(response.data?.message || 'Speaker created successfully.');
      }
      handleCloseModal();
      fetchSpeakers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save speaker');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCountryChange = (e) => {
    const nextCountryCode = e.target.value;
    const country = countries.find((item) => item.isoCode === nextCountryCode);
    setFormData((prev) => ({
      ...prev,
      countryCode: nextCountryCode,
      country: country?.name || '',
      stateCode: '',
      state: '',
      city: '',
    }));
  };

  const handleStateChange = (e) => {
    const nextStateCode = e.target.value;
    const state = states.find((item) => item.isoCode === nextStateCode);
    setFormData((prev) => ({
      ...prev,
      stateCode: nextStateCode,
      state: state?.name || '',
      city: '',
    }));
  };

  const handleCityChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      city: e.target.value,
    }));
  };

  const handleToggleActive = async (speaker) => {
    try {
      await api.patch(`/admin/speakers/${speaker.id}/toggle`, { 
        isActive: !speaker.isActive 
      });
      fetchSpeakers();
    } catch (error) {
      console.error('Failed to toggle speaker status:', error);
      alert(error.response?.data?.message || 'Failed to toggle speaker status');
    }
  };

  const columns = [
    {
      key: 'speaker',
      label: 'Speaker',
      render: (speaker) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {speaker.avatar ? (
            <img
              src={speaker.avatar}
              alt={speaker.name}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: theme.colors.primary + '20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <User size={24} color={theme.colors.primary} />
            </div>
          )}
          <div>
            <div style={{ fontWeight: '600', color: theme.colors.text }}>
              {speaker.name}
            </div>
            <div style={{ 
              fontSize: '13px', 
              color: theme.colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: '2px',
            }}>
              <Mail size={12} />
              {speaker.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      label: 'Contact',
      render: (speaker) => (
        <div>
          {speaker.phone ? (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              color: theme.colors.text,
            }}>
              <Phone size={14} color={theme.colors.textSecondary} />
              {speaker.phone}
            </div>
          ) : (
            <span style={{ color: theme.colors.textSecondary }}>-</span>
          )}
          {speaker.publicContactNumber && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: theme.colors.textSecondary,
              fontSize: '12px',
              marginTop: '4px',
            }}>
              <Phone size={12} />
              Public: {speaker.publicContactNumber}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'designation',
      label: 'Position',
      render: (speaker) => (
        <div>
          {speaker.designation || speaker.company ? (
            <>
              <div style={{ color: theme.colors.text, fontWeight: '500' }}>
                {speaker.designation || 'Speaker'}
              </div>
              {speaker.company && (
                <div style={{ 
                  fontSize: '13px', 
                  color: theme.colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '2px',
                }}>
                  <Briefcase size={12} />
                  {speaker.company}
                </div>
              )}
            </>
          ) : (
            <span style={{ color: theme.colors.textSecondary }}>-</span>
          )}
        </div>
      ),
    },
    {
      key: 'expertise',
      label: 'Expertise',
      render: (speaker) => (
        <div>
          {speaker.specialization || speaker.language ? (
            <>
              {speaker.specialization && (
                <div style={{ color: theme.colors.text, fontWeight: '500' }}>
                  {speaker.specialization}
                </div>
              )}
              {speaker.language && (
                <div style={{ 
                  fontSize: '13px', 
                  color: theme.colors.textSecondary,
                  marginTop: speaker.specialization ? '2px' : '0',
                }}>
                  {speaker.language}
                </div>
              )}
            </>
          ) : (
            <span style={{ color: theme.colors.textSecondary }}>-</span>
          )}
        </div>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (speaker) => {
        const locationLine = [speaker.city, speaker.state].filter(Boolean).join(', ');
        return locationLine || speaker.country ? (
          <div>
            <div style={{ color: theme.colors.text, fontWeight: '500' }}>{locationLine || '-'}</div>
            {speaker.country && (
              <div style={{ fontSize: '13px', color: theme.colors.textSecondary, marginTop: '2px' }}>
                {speaker.country}
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: theme.colors.textSecondary }}>-</span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (speaker) => (
        <Badge 
          variant={speaker.isActive ? 'success' : 'danger'} 
          size="sm"
          status
        >
          {speaker.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (speaker) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="sm"
            variant="ghost"
            icon={Edit3}
            onClick={() => handleOpenModal(speaker)}
            title="Edit"
          />
          <Button
            size="sm"
            variant={speaker.isActive ? 'ghost' : 'ghost'}
            icon={Power}
            onClick={() => handleToggleActive(speaker)}
            title={speaker.isActive ? 'Deactivate' : 'Activate'}
          />
        </div>
      ),
    },
  ];

  const availableSpeakerLanguages = Array.from(
    new Set(
      speakers.flatMap((speaker) => parseLanguageCsv(speaker.language || ''))
    )
  ).sort((a, b) => a.localeCompare(b));

  const filteredSpeakers = speakers.filter((speaker) => {
    const q = searchFilter.toLowerCase();
    const speakerLanguages = parseLanguageCsv(speaker.language || '').map((item) => item.toLowerCase());

    const matchesSearch =
      !q ||
      (speaker.name || '').toLowerCase().includes(q) ||
      (speaker.email || '').toLowerCase().includes(q) ||
      (speaker.specialization || '').toLowerCase().includes(q);

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && speaker.isActive) ||
      (statusFilter === 'inactive' && !speaker.isActive);

    const matchesLanguage =
      languageFilter === 'all' || speakerLanguages.includes(languageFilter.toLowerCase());

    return matchesSearch && matchesStatus && matchesLanguage;
  });

  const styles = {
    container: {
      padding: '32px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    actionBar: {
      marginBottom: '12px',
    },
    toolbarGrid: {
      display: 'grid',
      gridTemplateColumns: 'minmax(260px, 1fr) repeat(2, minmax(180px, 220px)) auto',
      gap: '10px',
      alignItems: 'end',
    },
    searchWrap: {
      position: 'relative',
      width: '100%',
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: theme.colors.textTertiary,
      pointerEvents: 'none',
    },
    searchInput: {
      width: '100%',
      height: '44px',
      padding: '10px 12px 10px 36px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    filterInput: {
      width: '100%',
      height: '44px',
      padding: '10px 12px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    addButtonWrap: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'stretch',
    },
    error: {
      padding: '12px 16px',
      borderRadius: '8px',
      backgroundColor: theme.colors.error + '15',
      color: theme.colors.error,
      fontSize: '14px',
      marginBottom: '20px',
      border: `1px solid ${theme.colors.error}30`,
    },
    success: {
      padding: '12px 16px',
      borderRadius: '8px',
      backgroundColor: theme.colors.success + '15',
      color: theme.colors.success,
      fontSize: '14px',
      marginBottom: '20px',
      border: `1px solid ${theme.colors.success}30`,
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
    },
    fullWidth: {
      gridColumn: '1 / -1',
    },
    selectField: {
      marginBottom: '20px',
      width: '100%',
    },
    selectLabel: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text,
    },
    selectInput: {
      width: '100%',
      padding: '11px 14px',
      fontSize: '14px',
      borderRadius: '8px',
      border: `2px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.inputBg,
      color: theme.colors.text,
      outline: 'none',
      fontFamily: 'inherit',
      colorScheme: theme.name === 'dark' ? 'dark' : 'light',
    },
    selectHelp: {
      marginTop: '6px',
      fontSize: '13px',
      color: theme.colors.textSecondary,
    },
  };

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading speakers..." />;
  }

  return (
    <div style={styles.container}>
      {successMessage && <div style={styles.success}>{successMessage}</div>}

      {/* Action Bar */}
      <Card compact>
        <div style={styles.actionBar}>
          <div>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: theme.colors.text,
              margin: 0,
            }}>
              All Speakers
            </h3>
          </div>
        </div>

        <div style={styles.toolbarGrid}>
          <div style={styles.searchWrap}>
            <Search size={16} style={styles.searchIcon} />
            <input
              style={styles.searchInput}
              placeholder="Search by name, email, specialization"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
          </div>
          <select
            style={styles.filterInput}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            style={styles.filterInput}
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
          >
            <option value="all">All Languages</option>
            {availableSpeakerLanguages.map((language) => (
              <option key={language} value={language.toLowerCase()}>{language}</option>
            ))}
          </select>
          <div style={styles.addButtonWrap}>
            <Button
              icon={Plus}
              onClick={() => handleOpenModal()}
            >
              Add Speaker
            </Button>
          </div>
        </div>
      </Card>

      {/* Speakers Table */}
      <div style={{ marginTop: '24px' }}>
        <Card>
          {filteredSpeakers.length === 0 ? (
            <EmptyState
              icon={UserPlus}
              title={speakers.length === 0 ? 'No speakers added yet' : 'No speakers match current filters'}
              description={speakers.length === 0 ? 'Add your first speaker to start organizing conference sessions.' : 'Try changing status/language filters or search text.'}
              action={() => handleOpenModal()}
              actionLabel="Add Speaker"
            />
          ) : (
            <Table
              columns={columns}
              data={filteredSpeakers}
              loading={loading}
              emptyMessage="No speakers found"
            />
          )}
        </Card>
      </div>

      {/* Create/Edit Speaker Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingSpeaker ? 'Edit Speaker' : 'Add New Speaker'}
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={submitting}
            >
              {editingSpeaker ? 'Save Changes' : 'Add Speaker'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          {!editingSpeaker && (
            <div style={styles.success}>
              After you add a speaker, a password setup email will be sent automatically.
            </div>
          )}

          <div style={styles.formGrid}>
            <div style={styles.fullWidth}>
              <Input
                type="text"
                label="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter speaker name"
                icon={User}
                required
              />
            </div>

            <Input
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="speaker@example.com"
              icon={Mail}
              required
            />

            <Input
              type="tel"
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1 (555) 000-0000"
              icon={Phone}
            />

            <div style={styles.selectField}>
              <label style={styles.selectLabel}>
                Country <span style={{ color: theme.colors.error }}> *</span>
              </label>
              <select
                value={formData.countryCode}
                onChange={handleCountryChange}
                required
                style={styles.selectInput}
              >
                <option value="">Select country</option>
                {countries.map((country) => (
                  <option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.selectField}>
              <label style={styles.selectLabel}>
                State <span style={{ color: theme.colors.error }}> *</span>
              </label>
              <select
                value={formData.stateCode}
                onChange={handleStateChange}
                required
                disabled={!formData.countryCode}
                style={styles.selectInput}
              >
                <option value="">Select state</option>
                {states.map((state) => (
                  <option key={state.isoCode} value={state.isoCode}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.selectField}>
              <label style={styles.selectLabel}>
                City <span style={{ color: theme.colors.error }}> *</span>
              </label>
              <select
                value={formData.city}
                onChange={handleCityChange}
                required
                disabled={!formData.stateCode}
                style={styles.selectInput}
              >
                <option value="">Select city</option>
                {cities.map((city) => (
                  <option key={`${city.name}-${city.latitude}-${city.longitude}`} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
              <div style={styles.selectHelp}>City and state are required for speaker onboarding.</div>
            </div>

            <Input
              type="text"
              label="Designation"
              value={formData.designation}
              onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
              placeholder="Software Engineer"
              icon={Briefcase}
            />

            <Input
              type="text"
              label="Company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Tech Corp"
              icon={Briefcase}
            />

            <LanguageMultiSelect
              label="Language"
              options={MEETING_LANGUAGE_OPTIONS}
              value={formData.language}
              onChange={(nextValue) => setFormData({ ...formData, language: nextValue })}
              helperText="Choose one or more languages from the standard list."
            />

            <Input
              type="text"
              label="Specialization"
              value={formData.specialization}
              onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
              placeholder="e.g., Cardiology"
            />

            <Input
              type="tel"
              label="Public Contact Number"
              value={formData.publicContactNumber}
              onChange={(e) => setFormData({ ...formData, publicContactNumber: e.target.value })}
              placeholder="+1 (555) 000-0000"
            />

            <div style={styles.fullWidth}>
              <Input
                type="url"
                label="Avatar URL"
                value={formData.avatar}
                onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                placeholder="https://example.com/avatar.jpg"
                helperText="Provide a URL to the speaker's profile image"
              />
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminSpeakers;
